import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState } from '@ui-kit';
import { useLintText } from '../hooks/useLintText';
import { LintResultTable } from '../components/LintResultTable';
import { ForbiddenCountBadge } from '../components/ForbiddenCountBadge';
import { lintRequestSchema } from '../schema/terminologySchema';
import type { ApiError } from '../types/terminology';

const MAX_LENGTH = 65536;
const DRAFT_STORAGE_KEY = 'terminologyManagement.checker.draft';

function readDraft(): string {
  try {
    return localStorage.getItem(DRAFT_STORAGE_KEY) ?? '';
  } catch {
    // localStorage 접근 불가 (프라이빗 모드 등) — 빈 값으로 시작
    return '';
  }
}

function writeDraft(text: string): void {
  try {
    if (text) {
      localStorage.setItem(DRAFT_STORAGE_KEY, text);
    } else {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    }
  } catch {
    // storage quota / 프라이빗 모드 — 임시 저장 생략
  }
}

/* ─── Main Page ─────────────────────────────────────────────────────────── */

export function TerminologyCheckerPage() {
  const { t } = useTranslation();
  const [text, setText] = useState<string>(readDraft);
  const [validationError, setValidationError] = useState<string | undefined>();
  const lintMutation = useLintText();

  useEffect(() => {
    writeDraft(text);
  }, [text]);

  const handleCheck = useCallback(() => {
    const parsed = lintRequestSchema.safeParse({ text });
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message);
      return;
    }
    setValidationError(undefined);
    lintMutation.mutate({ text: parsed.data.text });
  }, [text, lintMutation]);

  const handleClear = useCallback(() => {
    setText('');
    setValidationError(undefined);
    lintMutation.reset();
    writeDraft('');
  }, [lintMutation]);

  const isLoading = lintMutation.isPending;
  const isError = lintMutation.isError;
  const result = lintMutation.data;
  const hasResult = lintMutation.isSuccess && result !== undefined;
  const apiErr = lintMutation.error as unknown as ApiError | undefined;

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-4)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-family-sans)',
    cursor: 'pointer',
  };

  return (
    <div style={{ padding: 'var(--spacing-6)', maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader
        title={t('terminologyManagement.checker.title')}
        subtitle={t('terminologyManagement.checker.subtitle')}
        breadcrumb={<span>{t('terminologyManagement.checker.title')}</span>}
      />

      {/* Input */}
      <div style={{ marginTop: 'var(--spacing-5)' }}>
        <label htmlFor="lint-textarea" style={{ display: 'none' }}>
          {t('terminologyManagement.checker.textareaPlaceholder')}
        </label>
        <textarea
          id="lint-textarea"
          data-testid="lint-textarea"
          value={text}
          maxLength={MAX_LENGTH}
          placeholder={t('terminologyManagement.checker.textareaPlaceholder')}
          onChange={(e) => setText(e.target.value)}
          rows={12}
          aria-invalid={validationError ? true : undefined}
          aria-describedby={validationError ? 'lint-textarea-error' : undefined}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: 'var(--spacing-3)',
            border: `1px solid ${validationError ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
            borderRadius: 'var(--radius-md)',
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
            background: 'var(--color-surface-elevated)',
            resize: 'vertical',
          }}
        />

        {validationError && (
          <p id="lint-textarea-error" role="alert" style={{ margin: 'var(--spacing-1) 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}>
            {validationError}
          </p>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 'var(--spacing-2)',
            marginTop: 'var(--spacing-2)',
          }}
        >
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            {t('terminologyManagement.checker.charCount', {
              charCount: text.length.toLocaleString(),
              max: MAX_LENGTH.toLocaleString(),
            })}
          </span>
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button
              type="button"
              data-testid="lint-clear-btn"
              onClick={handleClear}
              disabled={!text}
              style={{
                ...inputStyle,
                background: 'var(--color-surface-muted)',
                color: 'var(--color-text-secondary)',
                opacity: text ? 1 : 0.5,
                cursor: text ? 'pointer' : 'not-allowed',
              }}
            >
              {t('terminologyManagement.checker.clearButton')}
            </button>
            <button
              type="button"
              data-testid="lint-check-btn"
              onClick={handleCheck}
              disabled={!text.trim() || isLoading}
              aria-busy={isLoading}
              style={{
                ...inputStyle,
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                fontWeight: 600,
                opacity: !text.trim() || isLoading ? 0.7 : 1,
                cursor: !text.trim() || isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading
                ? t('terminologyManagement.checker.checkButtonLoading')
                : t('terminologyManagement.checker.checkButton')}
            </button>
          </div>
        </div>
      </div>

      {/* Result area — 5 states (loading/empty/error/default 결과 표시, dialog N/A) */}
      <div style={{ marginTop: 'var(--spacing-6)' }}>
        {isLoading && (
          <div
            aria-busy="true"
            aria-label={t('terminologyManagement.checker.checkButtonLoading')}
            style={{
              background: 'var(--color-surface-muted)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-4)',
              minHeight: 160,
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        )}

        {!isLoading && isError && (
          <ErrorState
            title={t('terminologyManagement.error.network')}
            message={
              apiErr?.code === 'VALIDATION-400-001'
                ? t('terminologyManagement.error.VALIDATION-400-001')
                : apiErr?.message ?? t('terminologyManagement.error.network')
            }
            onRetry={handleCheck}
          />
        )}

        {!isLoading && !isError && hasResult && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-3)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--color-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {t('terminologyManagement.checker.resultTitle')}
              </span>
              <ForbiddenCountBadge count={result.summary.forbiddenCount} />
            </div>

            {result.violations.length === 0 ? (
              <div
                style={{
                  border: '1px solid var(--color-success)',
                  borderRadius: 'var(--radius-lg)',
                  background: 'color-mix(in srgb, var(--color-success) 6%, transparent)',
                }}
              >
                <EmptyState title={`✓ ${t('terminologyManagement.checker.noViolation')}`} />
              </div>
            ) : (
              <LintResultTable violations={result.violations} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
